import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  BarController,
  LineController,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(
  BarController,
  LineController,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

function ThreeDotsMenu({ chartRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handlePrint = () => {
    window.print();
    setOpen(false);
  };

  const handleDownload = () => {
    const chart = chartRef?.current;
    if (!chart) return;
    const url = chart.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = "size-chart.png";
    link.click();
    setOpen(false);
  };

  return (
    <div style={styles.menuWrapper} ref={ref}>
      <button
        style={styles.dots}
        onClick={() => setOpen(!open)}
        type="button"
        title="More options"
      >
        ⋮
      </button>
      {open && (
        <div style={styles.menu}>
          <div style={styles.menuIconRow}>
            <button
              style={styles.menuIconBtn}
              onClick={handlePrint}
              title="Print"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
            </button>
            <button
              style={styles.menuIconBtn}
              onClick={handleDownload}
              title="Download"
              type="button"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#555"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M19 16 v3 M17.5 19 h3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SizeChartCard({ itemName, sizes, onDragStart, onDragEnd }) {
  const chartRef = useRef();

  const labels = sizes.map((s) => s.size_name);
  const qtyData = sizes.map((s) => Number(s.qty) || 0);
  const amtData = sizes.map((s) => Number(s.sellingPrice) || 0);

  const maxQty = Math.max(...qtyData, 0);
  const maxAmt = Math.max(...amtData, 0);

  function getAxisConfig(val, { ticks = 5 } = {}) {
    if (!val || val <= 0) {
      return { max: ticks, step: 1 };
    }

    const rawStep = val / ticks;
    const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const norm = rawStep / mag;

    let niceNorm;
    if (norm <= 1) niceNorm = 1;
    else if (norm <= 2) niceNorm = 2;
    else if (norm <= 2.5) niceNorm = 2.5;
    else if (norm <= 5) niceNorm = 5;
    else niceNorm = 10;

    const step = niceNorm * mag;
    let max = Math.ceil(val / step) * step;
    if (max - val < step * 0.15) {
      max += step;
    }
    return { max, step };
  }

  function formatK(v) {
    if (v >= 1000000) return (v / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
    if (v >= 1000) return (v / 1000).toFixed(0) + "k";
    return v;
  }

  function formatQtyTick(v) {
    if (Number.isInteger(v)) return String(v);
    if (Math.abs(v) < 10) return Number(v).toFixed(1).replace(/\.0$/, "");
    return Number(v).toFixed(0);
  }

  const qtyAxis = getAxisConfig(maxQty);
  const amtAxis = getAxisConfig(maxAmt);

  const data = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "Amount",
        data: amtData,
        backgroundColor: "#5CC7C1",
        borderRadius: 2,
        barThickness: labels.length === 1 ? 48 : 28,
        yAxisID: "yRight",
        order: 2,
      },
      {
        type: "line",
        label: "Qty",
        data: qtyData,
        borderColor: "#5C74C7",
        backgroundColor: "#5C74C7",
        pointBackgroundColor: "#5C74C7",
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
        tension: 0,
        yAxisID: "yLeft",
        order: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          color: "#6b7280",
          padding: 12,
          font: { size: 11 },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.raw;
            if (ctx.dataset.label === "Amount") return ` Amount: ${formatK(v)}`;
            return ` Qty: ${v}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { color: "#9ca3af", font: { size: 11 } },
      },
      yLeft: {
        type: "linear",
        position: "left",
        min: 0,
        max: qtyAxis.max,
        grid: { color: "rgba(0,0,0,0.06)", drawBorder: false },
        ticks: {
          color: "#9ca3af",
          font: { size: 10 },
          stepSize: qtyAxis.step,
          callback: (v) => formatQtyTick(Number(v)),
        },
      },
      yRight: {
        type: "linear",
        position: "right",
        min: 0,
        max: amtAxis.max,
        grid: { display: false },
        ticks: {
          color: "#9ca3af",
          font: { size: 10 },
          stepSize: amtAxis.step,
          callback: (v) => formatK(v),
        },
      },
    },
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <button
          style={styles.dragHandle}
          draggable
          title="Drag to reorder chart"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          type="button"
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </button>
        <span style={styles.title}>{itemName}</span>
        <div style={styles.headerRight}>
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>
      <div style={styles.chartWrap}>
        <Chart ref={chartRef} type="bar" data={data} options={options} />
      </div>
    </div>
  );
}

export default function SizeCharts({ rows = [] }) {
  const items = useMemo(() => {
    const itemMap = new Map();

    for (const row of rows) {
      const rawItem = String(row?.item_name || "Unknown").trim() || "Unknown";
      const itemKey = rawItem.toLowerCase();

      if (!itemMap.has(itemKey)) {
        itemMap.set(itemKey, {
          itemName: rawItem,
          sizeMap: new Map(),
        });
      }

      const itemEntry = itemMap.get(itemKey);
      const rawSize = String(row?.size_name || "Unknown").trim() || "Unknown";
      const sizeKey = rawSize.toLowerCase();
      const qty = Number(row?.qty) || 0;
      const sellingPrice = Number(row?.sellingPrice) || Number(row?.selling_price) || 0;

      if (!itemEntry.sizeMap.has(sizeKey)) {
        itemEntry.sizeMap.set(sizeKey, {
          size_name: rawSize,
          qty: 0,
          sellingPrice: 0,
        });
      }

      const sizeEntry = itemEntry.sizeMap.get(sizeKey);
      sizeEntry.qty += qty;
      sizeEntry.sellingPrice += sellingPrice;
    }

    return Array.from(itemMap.values()).map((entry) => ({
      itemName: entry.itemName,
      sizes: Array.from(entry.sizeMap.values()),
    }));
  }, [rows]);

  const [chartOrder, setChartOrder] = useState([]);
  const [draggingChart, setDraggingChart] = useState(null);

  useEffect(() => {
    const available = items.map((i) => i.itemName);
    setChartOrder((prev) => {
      const kept = prev.filter((name) => available.includes(name));
      const added = available.filter((name) => !kept.includes(name));
      return [...kept, ...added];
    });
  }, [items]);

  const itemByName = useMemo(() => {
    const map = new Map();
    for (const item of items) map.set(item.itemName, item.sizes);
    return map;
  }, [items]);

  function handleDrop(targetName) {
    if (!draggingChart || draggingChart === targetName) return;
    const next = [...chartOrder];
    const from = next.indexOf(draggingChart);
    const to = next.indexOf(targetName);
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setChartOrder(next);
    setDraggingChart(null);
  }

  if (chartOrder.length === 0) {
    return <div style={styles.empty}>No chart data available.</div>;
  }

  return (
    <div style={styles.root}>
      <div style={styles.grid}>
        {chartOrder.map((itemName) => {
          const sizes = itemByName.get(itemName) || [];
          return (
            <div
              key={itemName}
              style={styles.colItem}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(itemName)}
            >
              <SizeChartCard
                itemName={itemName}
                sizes={sizes}
                onDragStart={() => setDraggingChart(itemName)}
                onDragEnd={() => setDraggingChart(null)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  root: {
    padding: "0 20px 16px",
    background: "#fff",
    fontFamily: "Segoe UI",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },
  colItem: {
    minWidth: 0,
    position: "relative",
  },
  card: {
    background: "#fff",
    border: "1px solid #f0f0f0",
    borderRadius: 12,
    padding: "20px 16px 16px",
    boxSizing: "border-box",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
    gap: 8,
  },
  headerRight: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
  },
  dragHandle: {
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: 4,
    lineHeight: 0,
    background: "transparent",
    border: "none",
    padding: 0,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: "#606060",
    lineHeight: "24px",
    marginTop: 2,
    marginBottom: 2,
  },
  chartWrap: {
    height: 400,
    position: "relative",
  },
  empty: {
    padding: 40,
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 14,
  },
  menuWrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dots: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 22,
    color: "#606060",
    padding: 0,
    lineHeight: 1,
    userSelect: "none",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 12px)",
    background: "#fff",
    border: "1px solid #e9e9e9",
    borderRadius: 8,
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.12)",
    zIndex: 50,
    padding: "8px 12px",
    minWidth: 110,
  },
  menuIconRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  menuIconBtn: {
    width: 34,
    height: 34,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    padding: 0,
  },
};
